// frontend/src/pages/Orders/OrderDetail.jsx - VERSIÓN CONSOLIDADA

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Table,
  Alert,
  Input,
} from 'reactstrap';
import { ordersAPI, invoiceSchedulesAPI } from '../../services/api';
import PDFViewerModal from '../../components/Modals/PDFViewerModal';
import InvoiceScheduleModal from '../../components/Modals/InvoiceScheduleModal';
import { generateOrderPDF } from '../../utils/pdfGenerator'; 
import { parseDate } from '../../utils/dates';

function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState('');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [scheduleEdit, setScheduleEdit] = useState({ scheduled_invoice_date: '', scheduled_amount: '' });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [deletingScheduleId, setDeletingScheduleId] = useState(null);
  const [uploadingOrderPdf, setUploadingOrderPdf] = useState(false);
  const [orderPdfMessage, setOrderPdfMessage] = useState(null);

  const startEditSchedule = (schedule) => {
    setEditingScheduleId(schedule.id);
    setScheduleEdit({
      scheduled_invoice_date: schedule.scheduled_invoice_date || '',
      scheduled_amount: schedule.scheduled_amount || '',
    });
  };

  const cancelEditSchedule = () => {
    setEditingScheduleId(null);
    setScheduleEdit({ scheduled_invoice_date: '', scheduled_amount: '' });
  };

  const saveScheduleEdit = async () => {
    if (!editingScheduleId) return;
    setSavingSchedule(true);
    try {
      await invoiceSchedulesAPI.patch(editingScheduleId, {
        scheduled_invoice_date: scheduleEdit.scheduled_invoice_date || null,
        scheduled_amount: scheduleEdit.scheduled_amount || 0,
      });
      await loadOrderDetail();
      cancelEditSchedule();
    } catch (err) {
      console.error('Error al editar parcialidad:', err);
      alert('No se pudo guardar la parcialidad. Verifica los datos.');
    } finally {
      setSavingSchedule(false);
    }
  };

  const deleteSchedule = async (schedule) => {
    const dateLabel = schedule.scheduled_invoice_date
      ? parseDate(schedule.scheduled_invoice_date + 'T00:00:00').toLocaleDateString('es-MX')
      : 'sin fecha';
    const confirmed = window.confirm(
      `¿Eliminar la parcialidad de ${formatCurrency(schedule.scheduled_amount)} (${dateLabel})?\n\n` +
      '• Se borrará toda su información de facturación y facilitación (fechas, sesiones, facilitador, lugar, etc.).\n' +
      '• Desaparecerá de la bandeja de facturas pendientes y de Facilitación.\n' +
      '• El monto regresará a "Pendiente" para que puedas volver a programarlo.\n\n' +
      'Esta acción no se puede deshacer.'
    );
    if (!confirmed) return;

    setDeletingScheduleId(schedule.id);
    try {
      const response = await invoiceSchedulesAPI.delete(schedule.id);
      if (editingScheduleId === schedule.id) cancelEditSchedule();
      await loadOrderDetail();
      alert(response.data?.message || 'Parcialidad eliminada. Ya puedes volver a programarla.');
    } catch (err) {
      console.error('Error al eliminar parcialidad:', err);
      alert(err.response?.data?.error || 'No se pudo eliminar la parcialidad.');
    } finally {
      setDeletingScheduleId(null);
    }
  };

  useEffect(() => {
    loadOrderDetail();
  }, [id]);

  const loadOrderDetail = async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getById(id);
      console.log('✅ Orden cargada con datos completos:', response.data);
      setOrder(response.data);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      alert('Error al cargar la orden');
      navigate('/admin/orders');
    } finally {
      setLoading(false);
    }
  };

  const resolveMediaUrl = (filePath) => {
    if (!filePath) return '';
    if (/^https?:\/\//i.test(filePath)) return filePath;

    const apiBaseUrl = process.env.REACT_APP_API_URL
      || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:8000');
    const normalizedPath = filePath.replace(/\\/g, '/');
    const mediaPath = normalizedPath.startsWith('/media/')
      ? normalizedPath
      : `/media/${normalizedPath.replace(/^\/?media\//, '').replace(/^\//, '')}`;

    return `${apiBaseUrl.replace(/\/$/, '')}${mediaPath}`;
  };

  const openPdfViewer = (pdfPath) => {
    const url = resolveMediaUrl(pdfPath);
    setSelectedPdfUrl(url);
    setPdfModalOpen(true);
  };

  const downloadPdf = (pdfPath, filename) => {
    const link = document.createElement('a');
    link.href = resolveMediaUrl(pdfPath);
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOrderPdfUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setOrderPdfMessage({ color: 'danger', text: 'Selecciona un archivo PDF valido.' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setOrderPdfMessage({ color: 'danger', text: 'El PDF no puede superar 10 MB.' });
      return;
    }

    setUploadingOrderPdf(true);
    setOrderPdfMessage(null);
    try {
      const response = await ordersAPI.attachPDF(order.id, file);
      setOrder(response.data.order);
      setOrderPdfMessage({ color: 'success', text: 'PDF de la orden guardado correctamente.' });
    } catch (error) {
      console.error('Error al subir PDF de la orden:', error);
      const apiError = error.response?.data?.error;
      const errorText = typeof apiError === 'string'
        ? apiError
        : apiError?.pdf_file?.[0] || 'No se pudo guardar el PDF de la orden.';
      setOrderPdfMessage({ color: 'danger', text: errorText });
    } finally {
      setUploadingOrderPdf(false);
    }
  };
    // ✅ NUEVA FUNCIÓN: Imprimir página
  const handlePrint = () => {
    window.print();
  };

  // ✅ NUEVA FUNCIÓN: Descargar PDF profesional
  const handleDownloadPDF = () => {
    if (!order || !consolidatedData) {
      alert('No hay datos disponibles para generar el PDF');
      return;
    }
    generateOrderPDF(order, consolidatedData);
  };

  const handleScheduleConfirm = async (scheduleData) => {
    try {
      await ordersAPI.scheduleInvoice(order.id, scheduleData);
      setScheduleModalOpen(false);
      await loadOrderDetail();
      alert('Facturación parcial programada correctamente');
    } catch (error) {
      console.error('Error al programar facturación parcial:', error);
      throw error;
    }
  };


  if (loading) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando información completa...</p>
          </div>
        </Container>
      </>
    );
  }

  if (!order) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <Card className="shadow border-0">
            <CardBody className="text-center py-5">
              <i className="ni ni-fat-remove text-danger" style={{ fontSize: '3rem' }}></i>
              <h4 className="mt-3">Orden no encontrada</h4>
              <Button 
                color="primary" 
                className="btn-icon mt-3"
                onClick={() => navigate('/admin/orders')}
              >
                <span className="btn-inner--icon">
                  <i className="ni ni-bold-left"></i>
                </span>
                <span className="btn-inner--text">Volver a Órdenes</span>
              </Button>
            </CardBody>
          </Card>
        </Container>
      </>
    );
  }

  // ✅ CONSOLIDAR DATOS DE TODAS LAS FUENTES
  const quote = order.quote_details;
  const invoiceSchedules = Array.isArray(order.invoice_schedules) ? order.invoice_schedules : [];
  const invoiceSchedule = invoiceSchedules[0] || order.invoice_schedule_details;
  const invoiceSchedulesList = invoiceSchedules.length > 0
    ? invoiceSchedules
    : (invoiceSchedule ? [invoiceSchedule] : []);
  const orderSubtotalAmount = parseFloat(order.subtotal ?? 0) || 0;
  const orderTotalWithTaxAmount = parseFloat(
    order.total ?? (orderSubtotalAmount * 1.16)
  ) || 0;
  const orderTotalAmount = orderSubtotalAmount;
  const scheduledAmountTotal = parseFloat(
    order.scheduled_amount_total ?? invoiceSchedulesList.reduce(
      (sum, schedule) => sum + (parseFloat(schedule?.scheduled_amount || 0) || 0),
      0
    )
  ) || 0;
  const remainingToSchedule = parseFloat(
    order.remaining_to_schedule ?? Math.max(orderSubtotalAmount - scheduledAmountTotal, 0)
  ) || 0;
  const canRequestAnotherInvoice = remainingToSchedule > 0;
  const formatCurrency = (amount) =>
    `$${(parseFloat(amount || 0) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  
  // 🔍 DEBUG TEMPORAL
  console.log('🔍 Invoice Schedule completo:', invoiceSchedule);
  console.log('🔍 Workshop Name:', invoiceSchedule?.workshop_name);
  console.log('🔍 Entry Requirements:', invoiceSchedule?.entry_requirements);
  console.log('🔍 Session Schedule:', invoiceSchedule?.session_schedule);
  console.log('🔍 Program Required:', invoiceSchedule?.program_required);
  console.log('🔍 Diplomas Required:', invoiceSchedule?.diplomas_required);

  const quoteItems = quote?.items || {};
  const serviceDetails = quoteItems.service_details || {};
  const clientInfo = quoteItems.client_info || {};
  const paymentInfo = quoteItems.payment || {};
  const quoteItemsList = quoteItems.items || [];

  // ✅ CONSOLIDAR INFORMACIÓN (prioridad: InvoiceSchedule > Quote > Order)
  const consolidatedData = {
    // Cliente
    clientName: clientInfo.client_name || quote?.client_name || order.billing_name || 'N/A',
    legalName: invoiceSchedule?.billing_name || clientInfo.legal_name || order.billing_name || 'N/A',
    rfc: invoiceSchedule?.billing_rfc || order.billing_rfc || 'N/A',
    address: invoiceSchedule?.billing_address || order.billing_address || 'N/A',
    
    // Contacto
    contactName: quote?.contact_name || clientInfo.contact_name || order.buyer_name || 'N/A',
    contactEmail: quote?.contact_email || clientInfo.contact_email || order.buyer_email || 'N/A',
    contactPhone: order.buyer_phone || 'N/A',
    
    // Vendedor
    sellerName: quote?.seller_name || order.seller_name || 'N/A',
    accountManager: quote?.account_manager || clientInfo.account_manager || 'N/A',
    
    // Servicio
    category: quote?.category || serviceDetails.category || 'N/A',
    serviceType: quote?.service_type || serviceDetails.service_type || 'N/A',
    totalHours: invoiceSchedule?.total_hours || serviceDetails.num_hours || 'N/A',
    numSessions: serviceDetails.num_sessions || 'N/A',
    numPeople: serviceDetails.num_people || 'N/A',
    numGroups: serviceDetails.num_groups || 'N/A',
    language: serviceDetails.language || 'N/A',
    cityState: serviceDetails.city_state || 'N/A',
    includes: serviceDetails.includes || '',
    
    // Logística (desde InvoiceSchedule)
    facilitator: invoiceSchedule?.facilitator_name || 'N/A',
    location: invoiceSchedule?.location || 'N/A',
    clientNeed: invoiceSchedule?.client_need || '',
    serviceDeliveryDate: invoiceSchedule?.service_delivery_date || null,
    
    // Descripción
    description: invoiceSchedule?.description || '',
    
    // Términos de pago
    paymentTerms: order.payment_terms || paymentInfo.payment_terms || 'N/A',
    creditDays: invoiceSchedule?.credit_days || paymentInfo.credit_days || quote?.credit_days || 0,
    incoterm: order.incoterm || 'N/A',
    
    // Fechas
    orderDate: order.order_date || null,
    deliveryDate: order.delivery_date || null,
    scheduledInvoiceDate: invoiceSchedule?.scheduled_invoice_date || null,
    
    // Otros
    supplierId: order.supplier_id || 'N/A',
    hourlyRate: invoiceSchedule?.hourly_rate || (serviceDetails.price_per_hour) || null,
    
    // Responsable de facturación
    responsibleUser: invoiceSchedule?.responsible_user_name || 'N/A',
  };

  return (
    <>
      {/* Header con gradiente corporativo */}
      <div className="min-height-300 bg-gradient-primary position-absolute w-100" style={{ zIndex: 0 }}></div>

      <Container fluid className="py-4" style={{ position: 'relative', zIndex: 1 }}>
        {/* Header con botones de acción */}
        <Row className="mb-3">
          <Col xs="12" md="6" className="d-flex align-items-center mb-2 mb-md-0">
            <Button
              color="white"
              size="sm"
              className="btn-icon shadow-sm"
              onClick={() => navigate('/admin/orders')}
            >
              <span className="btn-inner--icon">
                <i className="ni ni-bold-left"></i>
              </span>
              <span className="btn-inner--text ms-1">Volver a Órdenes</span>
            </Button>
          </Col>
          <Col xs="12" md="6" className="d-flex justify-content-md-end gap-2">
            {/* ✅ BOTÓN IMPRIMIR */}
            
            
            {/* ✅ BOTÓN DESCARGAR PDF */}
            <Button
              color="primary"
              size="sm"
              className="btn-icon shadow-sm"
              onClick={handleDownloadPDF}
            >
              <span className="btn-inner--icon">
                <i className="ni ni-cloud-download-95"></i>
              </span>
              <span className="btn-inner--text ms-1">Descargar PDF para impresion</span>
            </Button>
          </Col>
        </Row>


        {/* Banner de orden manual sin OC */}
        {!order.pdf_file && (
          <Row className="mb-3">
            <Col>
              <Alert color="warning" className="shadow-sm d-flex align-items-center justify-content-between mb-0">
                <div>
                  <i className="ni ni-bell-55 me-2"></i>
                  <strong>Esta orden no tiene documento de OC.</strong>{' '}
                  Sube el PDF para conservarlo en esta misma orden.
                </div>
                <Button
                  color="warning"
                  size="sm"
                  className="btn-icon ms-3 flex-shrink-0"
                  onClick={() => document.getElementById('order-pdf-upload').click()}
                  disabled={uploadingOrderPdf}
                >
                  <span className="btn-inner--icon"><i className="ni ni-cloud-upload-96"></i></span>
                  <span className="btn-inner--text ms-1">
                    {uploadingOrderPdf ? 'Subiendo...' : 'Subir OC'}
                  </span>
                </Button>
              </Alert>
            </Col>
          </Row>
        )}

        <Row className="printable-content">
          {/* ========== CONTENIDO PRINCIPAL ========== */}
          <Col lg="8">
            <Card className="shadow border-0 mb-4">
              {/* Header */}
              <CardHeader className="bg-gradient-primary pb-3">
                <Row className="align-items-center">
                  <Col md="8">
                    <h5 className="mb-0 text-white">
                      <i className="ni ni-box-2 me-2"></i>
                      Orden {order.order_number}
                    </h5>
                    <p className="text-sm text-white opacity-8 mb-0">
                      <i className="ni ni-calendar-grid-58 me-1"></i>
                      Creada el {parseDate(order.created_at).toLocaleDateString('es-MX')}
                    </p>
                  </Col>
                  <Col md="4" className="text-end">
                    {order.quote ? (
                      <Badge className="badge-lg bg-white text-primary shadow-sm">
                        <i className="ni ni-check-bold me-1"></i>
                        Con propuesta
                      </Badge>
                    ) : (
                      <Badge className="badge-lg bg-white text-warning shadow-sm">
                        <i className="ni ni-fat-remove me-1"></i>
                        Sin Propuesta
                      </Badge>
                    )}
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="p-4">
                {/* ========== 1. INFORMACIÓN DEL CLIENTE ========== */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-building me-2"></i>
                    Información del Cliente
                  </h6>
                  <Row>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Cliente:</strong> 
                        <span className="text-muted ms-2">{consolidatedData.clientName}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Razón Social:</strong> 
                        <span className="text-muted ms-2">{consolidatedData.legalName}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">RFC:</strong> 
                        <Badge color="primary" className="badge-sm ms-2">
                          {consolidatedData.rfc}
                        </Badge>
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Contacto:</strong> 
                        <span className="text-muted ms-2">{consolidatedData.contactName}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Email:</strong> 
                        <span className="text-muted ms-2">{consolidatedData.contactEmail}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Teléfono:</strong> 
                        <span className="text-muted ms-2">{consolidatedData.contactPhone}</span>
                      </p>
                    </Col>
                  </Row>
                  <p className="text-sm mb-0 mt-2">
                    <strong className="text-dark">
                      <i className="ni ni-pin-3 me-2"></i>
                      Dirección Fiscal:
                    </strong> 
                    <span className="text-muted ms-2">{consolidatedData.address}</span>
                  </p>
                </div>

                <hr className="horizontal dark" />

                {/* ========== 2. VENDEDOR Y GESTIÓN ========== */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-badge me-2"></i>
                    Vendedor y Gestión
                  </h6>
                  <Row>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Account Manager:</strong> 
                        <Badge color="primary" className="ms-2">
                          {consolidatedData.sellerName}
                        </Badge>
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">ID Proveedor:</strong> 
                        <Badge color="primary" className="badge-sm ms-2">
                          {consolidatedData.supplierId}
                        </Badge>
                      </p>
                        
                      
                    </Col>
                  </Row>
                </div>

                <hr className="horizontal dark" />

                {/* ========== 3. DETALLES DEL SERVICIO ========== */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-books me-2"></i>
                    Detalles del Servicio
                  </h6>
                  <Row>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Categoría:</strong> 
                        <Badge color="primary" className="ms-2">
                          {consolidatedData.category}
                        </Badge>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Tipo de Servicio:</strong> 
                        <span className="text-muted ms-2">{consolidatedData.serviceType}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Horas Totales:</strong> 
                        <Badge color="primary" className="ms-2">
                          {consolidatedData.totalHours} hrs
                        </Badge>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Sesiones:</strong> 
                        <span className="text-muted ms-2">{consolidatedData.numSessions}</span>
                      </p>
                    </Col>
                    <Col md="6">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Personas:</strong> 
                        <span className="text-muted ms-2">{consolidatedData.numPeople}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Grupos:</strong> 
                        <span className="text-muted ms-2">{consolidatedData.numGroups}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Idioma:</strong> 
                        <span className="text-muted ms-2">{consolidatedData.language}</span>
                      </p>
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Ciudad:</strong> 
                        <span className="text-muted ms-2">{consolidatedData.cityState}</span>
                      </p>
                    </Col>
                  </Row>
                  {consolidatedData.includes && (
                    <p className="text-sm mb-0 mt-2">
                      <strong className="text-dark">Incluye:</strong>
                      <span className="text-muted ms-2">{consolidatedData.includes}</span>
                    </p>
                  )}
                  {consolidatedData.hourlyRate && (
                    <p className="text-sm mb-0 mt-2">
                      <strong className="text-dark">Costo por Hora:</strong>
                      <Badge color="primary" className="ms-2">
                        ${parseFloat(consolidatedData.hourlyRate).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </Badge>
                    </p>
                  )}
                </div>

                <hr className="horizontal dark" />

                                {/* ========== 4. LOGÍSTICA Y ENTREGA ========== */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-delivery-fast me-2"></i>
                    Logística y Entrega
                  </h6>
                  <Row>
                    <Col md="6">
                      {consolidatedData.serviceDeliveryDate && (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Fecha de Impartición:</strong> 
                          <span className="text-muted ms-2">
                            {parseDate(consolidatedData.serviceDeliveryDate).toLocaleDateString('es-MX')}
                          </span>
                        </p>
                      )}
                      {consolidatedData.orderDate && (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Fecha de Orden:</strong> 
                          <span className="text-muted ms-2">
                            {parseDate(consolidatedData.orderDate).toLocaleDateString('es-MX')}
                          </span>
                        </p>
                      )}
                      {consolidatedData.deliveryDate && (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Fecha de Entrega:</strong> 
                          <span className="text-muted ms-2">
                            {parseDate(consolidatedData.deliveryDate).toLocaleDateString('es-MX')}
                          </span>
                        </p>
                      )}
                      {consolidatedData.scheduledInvoiceDate && (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">Fecha Programada Factura:</strong> 
                          <Badge color="primary" className="ms-2">
                            {parseDate(consolidatedData.scheduledInvoiceDate).toLocaleDateString('es-MX')}
                          </Badge>
                        </p>
                      )}
                    </Col>
                    <Col md="6">
                      {/* ✅ PRIORIDAD: order.facilitator_name > invoiceSchedule.facilitator_name */}
                      {(order.facilitator_name || consolidatedData.facilitator) !== 'N/A' && (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">
                            <i className="ni ni-single-02 me-1"></i>
                            Facilitador:
                          </strong> 
                          <Badge color="primary" className="ms-2">
                            {order.facilitator_name || consolidatedData.facilitator}
                          </Badge>
                        </p>
                      )}
                      {/* ✅ PRIORIDAD: order.location > invoiceSchedule.location */}
                      {(order.location || consolidatedData.location) && (
                        <p className="text-sm mb-2">
                          <strong className="text-dark">
                            <i className="ni ni-pin-3 me-1"></i>
                            Lugar:
                          </strong> 
                          <span className="text-muted ms-2">
                            {order.location || consolidatedData.location}
                          </span>
                        </p>
                      )}
                    </Col>
                  </Row>
                </div>

                <hr className="horizontal dark" />

                {/* ========== FACTURACIÓN PARCIAL ========== */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-money-coins me-2"></i>
                    Avance de Facturación
                  </h6>
                  <Row className="mb-3">
                    <Col md="4">
                      <div className="text-center p-3 bg-light border-radius-lg shadow-sm">
                        <p className="text-xs text-uppercase mb-1 text-muted">Total OC (sin IVA)</p>
                        <h6 className="mb-0 text-dark">{formatCurrency(orderTotalAmount)}</h6>
                      </div>
                    </Col>
                    <Col md="4">
                      <div className="text-center p-3 bg-light border-radius-lg shadow-sm">
                        <p className="text-xs text-uppercase mb-1 text-muted">Programado</p>
                        <h6 className="mb-0 text-dark">{formatCurrency(scheduledAmountTotal)}</h6>
                      </div>
                    </Col>
                    <Col md="4">
                      <div className={`text-center p-3 border-radius-lg shadow-sm ${remainingToSchedule > 0 ? 'bg-gradient-primary' : 'bg-gradient-primary'}`}>
                        <p className="text-xs text-uppercase mb-1 text-white opacity-8">Pendiente</p>
                        <h6 className="mb-0 text-white">{formatCurrency(remainingToSchedule)}</h6>
                      </div>
                    </Col>
                  </Row>
                  <small className="text-muted d-block mt-2">
                    Total OC con IVA: {formatCurrency(orderTotalWithTaxAmount)}
                  </small>

                  {invoiceSchedulesList.length > 0 ? (
                    <div className="table-responsive">
                      <Table className="align-items-center table-flush" size="sm">
                        <thead className="thead-light">
                          <tr>
                            <th>#</th>
                            <th>Fecha Programada</th>
                            <th className="text-end">Monto</th>
                            <th className="text-center">Estado</th>
                            <th className="text-center">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceSchedulesList.map((schedule, index) => {
                            const isLocked = schedule.status === 'invoice_uploaded' || schedule.status === 'paid';
                            const isEditing = editingScheduleId === schedule.id;
                            const invoiceFolios = (schedule.invoices || [])
                              .map(inv => (inv.serie ? `${inv.serie}-${inv.folio}` : inv.folio))
                              .join(', ');
                            return (
                              <tr key={schedule.id || index}>
                                <td className="text-sm">{index + 1}</td>
                                <td className="text-sm">
                                  {isEditing ? (
                                    <Input
                                      type="date"
                                      bsSize="sm"
                                      value={scheduleEdit.scheduled_invoice_date}
                                      onChange={e => setScheduleEdit(prev => ({ ...prev, scheduled_invoice_date: e.target.value }))}
                                    />
                                  ) : schedule.scheduled_invoice_date ? (
                                    parseDate(schedule.scheduled_invoice_date + 'T00:00:00').toLocaleDateString('es-MX')
                                  ) : 'N/A'}
                                </td>
                                <td className="text-sm text-end">
                                  {isEditing ? (
                                    <Input
                                      type="number"
                                      step="0.01"
                                      bsSize="sm"
                                      value={scheduleEdit.scheduled_amount}
                                      onChange={e => setScheduleEdit(prev => ({ ...prev, scheduled_amount: e.target.value }))}
                                    />
                                  ) : (
                                    formatCurrency(schedule.scheduled_amount)
                                  )}
                                </td>
                                <td className="text-center">
                                  <Badge
                                    color={
                                      schedule.status === 'invoice_uploaded' ? 'success' :
                                      schedule.status === 'overdue' ? 'danger' :
                                      schedule.status === 'notified' ? 'warning' :
                                      'info'
                                    }
                                    className="badge-sm"
                                  >
                                    {schedule.status === 'invoice_uploaded' ? 'Facturada' :
                                     schedule.status === 'overdue' ? 'Vencida' :
                                     schedule.status === 'notified' ? 'Notificada' :
                                     'Pendiente'}
                                  </Badge>
                                  {invoiceFolios && (
                                    <small className="d-block text-xs text-dark mt-1" title={(schedule.invoices || []).map(inv => inv.uuid).filter(Boolean).join(', ')}>
                                      Factura: <strong>{invoiceFolios}</strong>
                                    </small>
                                  )}
                                </td>
                                <td className="text-center">
                                  {isLocked ? (
                                    <small className="text-muted text-xs">
                                      <i className="ni ni-lock-circle-open me-1" />
                                      {invoiceFolios ? `Facturada (folio ${invoiceFolios})` : 'Facturada'}
                                    </small>
                                  ) : isEditing ? (
                                    <>
                                      <Button
                                        color="success"
                                        size="sm"
                                        className="btn-icon shadow-sm me-1"
                                        onClick={saveScheduleEdit}
                                        disabled={savingSchedule}
                                        title="Guardar"
                                      >
                                      
                                        Guardar
                                      </Button>
                                      <Button
                                        color="secondary"
                                        size="sm"
                                        className="btn-icon shadow-sm"
                                        onClick={cancelEditSchedule}
                                        disabled={savingSchedule}
                                        title="Cancelar"
                                      >
                                        
                                        Cancelar
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        color="warning"
                                        size="sm"
                                        className="shadow-sm me-1"
                                        onClick={() => startEditSchedule(schedule)}
                                        disabled={deletingScheduleId === schedule.id}
                                        title="Editar fecha y monto"
                                      >
                                        Editar
                                      </Button>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        className="shadow-sm"
                                        onClick={() => deleteSchedule(schedule)}
                                        disabled={deletingScheduleId === schedule.id}
                                        title="Eliminar parcialidad para volver a programarla"
                                      >
                                        {deletingScheduleId === schedule.id ? 'Eliminando...' : 'Eliminar'}
                                      </Button>
                                    </>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="bg-light p-3 border-radius-lg">
                      <p className="text-sm text-muted mb-0">Aún no hay parcialidades programadas.</p>
                    </div>
                  )}
                </div>

                <hr className="horizontal dark" />

                {/* ========== 🆕 DETALLES DEL TALLER ========== */}
                {(invoiceSchedule?.workshop_name || 
                  invoiceSchedule?.entry_requirements || 
                  invoiceSchedule?.session_schedule) && (
                  <>
                    <div className="mb-4">
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                        <i className="ni ni-hat-3 me-2"></i>
                        Detalles del Taller
                      </h6>
                      <Row>
                        <Col md="6">
                          {invoiceSchedule?.workshop_name && (
                            <p className="text-sm mb-2">
                              <strong className="text-dark">Nombre del Taller:</strong> 
                              <span className="text-muted ms-2">{invoiceSchedule.workshop_name}</span>
                            </p>
                          )}
                          {invoiceSchedule?.session_schedule && (
                            <p className="text-sm mb-2">
                              <strong className="text-dark">
                                <i className="ni ni-time-alarm me-1"></i>
                                Horario:
                              </strong> 
                              <Badge color="primary" className="ms-2">
                                {invoiceSchedule.session_schedule}
                              </Badge>
                            </p>
                          )}
                        </Col>
                        <Col md="6">
                          {invoiceSchedule?.program_required !== undefined && (
                            <p className="text-sm mb-2">
                              <strong className="text-dark">Programa:</strong> 
                              <Badge color={invoiceSchedule.program_required ? 'success' : 'secondary'} className="ms-2">
                                {invoiceSchedule.program_required ? 'Sí requerido' : 'No requerido'}
                              </Badge>
                            </p>
                          )}
                          {invoiceSchedule?.diplomas_required !== undefined && (
                            <p className="text-sm mb-2">
                              <strong className="text-dark">Diplomas:</strong> 
                              <Badge color={invoiceSchedule.diplomas_required ? 'success' : 'secondary'} className="ms-2">
                                {invoiceSchedule.diplomas_required ? 'Sí requeridos' : 'No requeridos'}
                              </Badge>
                            </p>
                          )}
                        </Col>
                      </Row>
                      {invoiceSchedule?.entry_requirements && (
                        <div className="bg-gradient-light p-3 border-radius-lg shadow-sm mt-3">
                          <p className="text-xs text-uppercase text-muted mb-1">
                            <i className="ni ni-check-bold me-1"></i>
                            Requisitos de Ingreso
                          </p>
                          <p className="text-sm mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                            {invoiceSchedule.entry_requirements}
                          </p>
                        </div>
                      )}
                    </div>
                    <hr className="horizontal dark" />
                  </>
                )}

                


                {/* ========== 5. NECESIDAD DEL CLIENTE ========== */}
                {/* ✅ PRIORIDAD: order.client_need > invoiceSchedule.client_need */}
                {(order.client_need || consolidatedData.clientNeed) && (
                  <>
                    <div className="mb-4">
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                        <i className="ni ni-bulb-61 me-2"></i>
                        Necesidad del Cliente
                      </h6>
                      <div className="bg-gradient-light p-4 border-radius-lg shadow-sm">
                        <p className="text-sm mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                          {order.client_need || consolidatedData.clientNeed}
                        </p>
                      </div>
                    </div>
                    <hr className="horizontal dark" />
                  </>
                )}

                {/* ========== 6. DESCRIPCIÓN DEL SERVICIO ========== */}
                {consolidatedData.description && (
                  <>
                    <div className="mb-4">
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                        <i className="ni ni-align-left-2 me-2"></i>
                        Descripción del Servicio
                      </h6>
                      <div className="bg-light p-3 border-radius-lg">
                        <p className="text-sm mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                          {consolidatedData.description}
                        </p>
                      </div>
                    </div>
                    <hr className="horizontal dark" />
                  </>
                )}


                {/* ========== 6. ITEMS DETALLADOS ========== */}
                {quoteItemsList.length > 0 && (
                  <>
                    <div className="mb-4">
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                        <i className="ni ni-bullet-list-67 me-2"></i>
                        Items del Servicio
                      </h6>
                      <div className="table-responsive">
                        <Table className="align-items-center table-flush" size="sm">
                          <thead className="thead-light">
                            <tr>
                              <th>Descripción</th>
                              <th className="text-center">Cantidad</th>
                              <th className="text-end">Precio Unit.</th>
                              <th className="text-end">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quoteItemsList.map((item, index) => (
                              <tr key={index}>
                                <td className="text-sm">{item.description || 'N/A'}</td>
                                <td className="text-center text-sm">
                                  {item.quantity || 1} {item.unit || 'EA'}
                                </td>
                                <td className="text-end text-sm">
                                  ${parseFloat(item.unit_price || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </td>
                                <td className="text-end text-sm font-weight-bold">
                                  ${parseFloat(item.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                    <hr className="horizontal dark" />
                  </>
                )}

                {/* ========== 7. TÉRMINOS COMERCIALES ========== */}
                <div className="mb-4">
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-credit-card me-2"></i>
                    Términos Comerciales
                  </h6>
                  <Row>
                    <Col md="4">
                      <p className="text-sm mb-2">
                        <strong className="text-dark">Términos de Pago:</strong> 
                        <Badge color="primary" className="ms-2">
                          {consolidatedData.paymentTerms}
                        </Badge>
                      </p>
                    </Col>
                   
                    
                  </Row>
                </div>

                <hr className="horizontal dark" />

                {/* ========== 8. MONTOS ========== */}
                <div>
                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                    <i className="ni ni-money-coins me-2"></i>
                    Información de Pago
                  </h6>
                  <Row className="mt-3">
                    <Col md="4">
                      <div className="text-center p-3 bg-light border-radius-lg shadow-sm">
                        <p className="text-xs text-uppercase mb-1 text-muted">Subtotal</p>
                        <h6 className="mb-0 text-dark">
                          ${parseFloat(order.subtotal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </h6>
                      </div>
                    </Col>
                    <Col md="4">
                      <div className="text-center p-3 bg-light border-radius-lg shadow-sm">
                        <p className="text-xs text-uppercase mb-1 text-muted">IVA (16%)</p>
                        <h6 className="mb-0 text-dark">
                          {/* ✅ CALCULAR IVA EN FRONTEND */}
                          ${(parseFloat(order.subtotal || 0) * 0.16).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </h6>
                      </div>
                    </Col>
                    <Col md="4">
                      <div className="text-center p-3 bg-gradient-primary border-radius-lg shadow">
                        <p className="text-xs text-uppercase mb-1 text-white opacity-8">Total</p>
                        <h6 className="mb-0 text-white font-weight-bold">
                          {/* ✅ CALCULAR TOTAL EN FRONTEND */}
                          ${(parseFloat(order.subtotal || 0) * 1.16).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </h6>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* ========== 9. NOTAS ADICIONALES ========== */}
                {(order.notes || invoiceSchedule?.notes) && (
                  <>
                    <hr className="horizontal dark" />
                    <div>
                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                        <i className="ni ni-align-left-2 me-2"></i>
                        Notas Adicionales
                      </h6>
                      {order.notes && (
                        <div className="bg-light p-3 border-radius-lg mb-2">
                          <p className="text-xs text-uppercase text-muted mb-1">Notas de la Orden</p>
                          <p className="text-sm mb-0 text-dark">{order.notes}</p>
                        </div>
                      )}
                      {invoiceSchedule?.notes && (
                        <div className="bg-light p-3 border-radius-lg">
                          <p className="text-xs text-uppercase text-muted mb-1">Notas de Facturación</p>
                          <p className="text-sm mb-0 text-dark">{invoiceSchedule.notes}</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* ========== SIDEBAR DERECHO ========== */}
          <Col lg="4">
            {/* Card de archivos */}
            <Card className="shadow border-0 mb-4">
              <CardHeader className="bg-gradient-primary pb-3">
                <h6 className="mb-0 text-white">
                  <i className="ni ni-folder-17 me-2"></i>
                  Archivos
                </h6>
              </CardHeader>
              <CardBody className="p-3">
                {order.pdf_file ? (
                  <>
                    <Button
                      color="primary"
                      size="sm"
                      block
                      className="mb-2 btn-icon shadow-sm"
                      onClick={() => openPdfViewer(order.pdf_file)}
                    >
                      <span className="btn-inner--icon">
                        <i className="ni ni-single-copy-04"></i>
                      </span>
                      <span className="btn-inner--text ms-2">Ver PDF Orden</span>
                    </Button>
                    <Button
                      color="info"
                      size="sm"
                      block
                      className="mb-2 btn-icon shadow-sm"
                      onClick={() => downloadPdf(order.pdf_file, `${order.order_number}.pdf`)}
                    >
                      <span className="btn-inner--icon">
                        <i className="ni ni-cloud-download-95"></i>
                      </span>
                      <span className="btn-inner--text ms-2">Descargar PDF</span>
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-3">
                    <i className="ni ni-folder-17 text-muted" style={{ fontSize: '2rem' }}></i>
                    <p className="text-xs text-muted mb-2 mt-2">No hay PDF disponible</p>
                    <input
                      id="order-pdf-upload"
                      type="file"
                      accept="application/pdf,.pdf"
                      className="d-none"
                      onChange={handleOrderPdfUpload}
                      disabled={uploadingOrderPdf}
                    />
                    <Button
                      color="warning"
                      size="sm"
                      className="btn-icon shadow-sm"
                      onClick={() => document.getElementById('order-pdf-upload').click()}
                      disabled={uploadingOrderPdf}
                    >
                      <span className="btn-inner--icon">
                        <i className="ni ni-cloud-upload-96"></i>
                      </span>
                      <span className="btn-inner--text ms-2">
                        {uploadingOrderPdf ? 'Subiendo...' : 'Subir PDF de la OC'}
                      </span>
                    </Button>
                  </div>
                )}

                {orderPdfMessage && (
                  <Alert color={orderPdfMessage.color} className="py-2 px-3 mt-2 mb-2 text-sm">
                    {orderPdfMessage.text}
                  </Alert>
                )}

                {order.xml_file && (
                  <Button
                    color="secondary"
                    size="sm"
                    block
                    className="btn-icon shadow-sm mb-2"
                    onClick={() => downloadPdf(order.xml_file, `${order.order_number}.xml`)}
                  >
                    <span className="btn-inner--icon">
                      <i className="ni ni-archive-2"></i>
                    </span>
                    <span className="btn-inner--text ms-2">Descargar XML</span>
                  </Button>
                )}

                {/* PDF de propuesta */}
                {quote && quote.pdf_file && (
                  <>
                    <hr className="horizontal dark my-2" />
                    <small className="text-uppercase text-xs font-weight-bold text-muted d-block mb-2">
                      Propuesta Original
                    </small>
                    <Button
                                            color="info"
                      size="sm"
                      block
                      outline
                      className="btn-icon shadow-sm"
                      onClick={() => openPdfViewer(quote.pdf_file)}
                    >
                      <span className="btn-inner--icon">
                        <i className="ni ni-single-copy-04"></i>
                      </span>
                      <span className="btn-inner--text ms-2">Ver PDF Propuesta</span>
                    </Button>
                  </>
                )}
              </CardBody>
            </Card>

            {/* Card de cotización vinculada */}
            {quote && (
              <Card className="shadow border-0 mb-4">
                <CardHeader className="bg-gradient-primary pb-3">
                  <h6 className="mb-0 text-white">
                    <i className="ni ni-single-copy-04 me-2"></i>
                    Propuesta Vinculada
                  </h6>
                </CardHeader>
                <CardBody className="p-3">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon icon-shape bg-gradient-info shadow text-center border-radius-md">
                      <i className="ni ni-single-copy-04 text-white text-lg opacity-10"></i>
                    </div>
                    <div className="ms-3">
                      <p className="text-xs text-muted mb-0">Propuesta</p>
                      <h6 className="mb-0 text-dark">{quote.proposal_number}</h6>
                      <p className="text-xs text-muted mb-0">
                        <Badge color="primary" className="badge-sm">
                          Versión {quote.version}
                        </Badge>
                      </p>
                    </div>
                  </div>

                  <div className="bg-light p-2 border-radius-lg mb-3">
                    <p className="text-xs mb-1">
                      <strong>Estado:</strong>{' '}
                      <Badge 
                        color={
                          quote.status === 'accepted' ? 'primary' :
                          quote.status === 'rejected' ? 'primary' :
                          quote.status === 'archived' ? 'primary' :
                          'warning'
                        }
                        className="badge-sm"
                      >
                        {quote.status === 'accepted' ? 'Aceptada' :
                         quote.status === 'rejected' ? 'Rechazada' :
                         quote.status === 'archived' ? 'Archivada' :
                         'Pendiente'}
                      </Badge>
                    </p>
                    <p className="text-xs mb-1">
                      <strong>Fecha:</strong> {quote.quote_date ? parseDate(quote.quote_date).toLocaleDateString('es-MX') : 'N/A'}
                    </p>
                    <p className="text-xs mb-0">
                      <strong>Total:</strong> ${parseFloat(quote.subtotal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </p>
                  </div>

                  <Button
                    color="info"
                    size="sm"
                    block
                    className="btn-icon shadow-sm"
                    onClick={() => navigate(`/admin/quotes/${order.quote}`)}
                  >
                    <span className="btn-inner--icon">
                      <i className="ni ni-bold-right"></i>
                    </span>
                    <span className="btn-inner--text ms-2">Ver Propuesta Completa</span>
                  </Button>
                </CardBody>
              </Card>
            )}

            {/* Card de facturación programada */}
            {(invoiceSchedulesList.length > 0 || canRequestAnotherInvoice) && (
              <Card className="shadow border-0 mb-4">
                <CardHeader className="bg-gradient-primary pb-3">
                  <h6 className="mb-0 text-white">
                    <i className="ni ni-calendar-grid-58 me-2"></i>
                    Facturación Programada
                  </h6>
                </CardHeader>
                <CardBody className="p-3">
                  <div className="bg-light p-2 border-radius-lg mb-3">
                    <p className="text-xs mb-1">
                      <strong>Total OC (sin IVA):</strong> {formatCurrency(orderTotalAmount)}
                    </p>
                    <p className="text-xs mb-1">
                      <strong>Programado:</strong> {formatCurrency(scheduledAmountTotal)}
                    </p>
                    <p className="text-xs mb-0">
                      <strong>Pendiente:</strong> {formatCurrency(remainingToSchedule)}
                    </p>
                    <p className="text-xs mb-0 mt-1">
                      <strong>Total OC (con IVA):</strong> {formatCurrency(orderTotalWithTaxAmount)}
                    </p>
                  </div>

                  {canRequestAnotherInvoice ? (
                    <Button
                      color="warning"
                      size="sm"
                      block
                      className="btn-icon shadow-sm mb-2"
                      onClick={() => setScheduleModalOpen(true)}
                    >
                      <span className="btn-inner--icon">
                        <i className="ni ni-fat-add"></i>
                      </span>
                      <span className="btn-inner--text ms-2">Solicitar otra factura</span>
                    </Button>
                  ) : (
                    <div className="text-center p-2 border-radius-lg mb-2 bg-gradient-primary">
                      <p className="text-xs text-white mb-0">
                        <i className="ni ni-check-bold me-1"></i>
                        OC completa: ya no hay saldo pendiente por programar
                      </p>
                    </div>
                  )}

                  {invoiceSchedulesList.length > 0 && (
                    <small className="text-muted d-block">
                      Parcialidades registradas: <strong>{invoiceSchedulesList.length}</strong>
                    </small>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Card de factura registrada */}
            {order.has_invoice && (
              <Card className="shadow border-0 mb-4">
                <CardHeader className="bg-gradient-primary pb-3">
                  <h6 className="mb-0 text-white">
                    <i className="ni ni-money-coins me-2"></i>
                    Factura Registrada
                  </h6>
                </CardHeader>
                <CardBody className="p-3">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon icon-shape bg-gradient-primary shadow text-center border-radius-md">
                      <i className="ni ni-money-coins text-white text-lg opacity-10"></i>
                    </div>
                    <div className="ms-3">
                      <p className="text-xs text-muted mb-0">Estado</p>
                      <h6 className="mb-0 text-dark">
                        <Badge color="primary">
                          <i className="ni ni-check-bold me-1"></i>
                          Con factura
                        </Badge>
                      </h6>
                    </div>
                  </div>

                  <div className=" mb-3" role="alert">
                    <span className="alert-icon"><i className="ni ni-check-bold"></i></span>
                    <span className="alert-text text-xs">
                      Esta orden ya tiene al menos una factura registrada
                    </span>
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Card de información de matching */}
            
            
          </Col>
        </Row>
      </Container>

      {/* Modal visor de PDF */}
      <PDFViewerModal
        isOpen={pdfModalOpen}
        toggle={() => setPdfModalOpen(false)}
        pdfUrl={selectedPdfUrl}
        title={`Orden ${order.order_number}`}
      />

      <InvoiceScheduleModal
        isOpen={scheduleModalOpen}
        toggle={() => setScheduleModalOpen(false)}
        order={order}
        quoteData={quote}
        onConfirm={handleScheduleConfirm}
      />

      {/* ✅ ESTILOS PARA IMPRESIÓN */}
      <style>{`
        @media print {
          /* Ocultar elementos innecesarios */
          .sidebar,
          .navbar,
          .btn,
          .badge,
          .card-header .bg-gradient-primary,
          .min-height-300,
          .shadow {
            display: none !important;
          }

          /* Ajustar contenedor */
          .container-fluid {
            max-width: 100%;
            padding: 0;
            margin: 0;
          }

          /* Mostrar todo el contenido */
          .printable-content {
            display: block !important;
            width: 100% !important;
          }

          /* Ajustar columnas */
          .col-lg-8,
          .col-lg-4 {
            width: 100% !important;
            max-width: 100% !important;
          }

          /* Ajustar cards */
          .card {
            border: 1px solid #dee2e6 !important;
            box-shadow: none !important;
            page-break-inside: avoid;
            margin-bottom: 1rem;
          }

          .card-body {
            padding: 1rem !important;
          }

          /* Ajustar tipografía */
          body {
            font-size: 11pt;
            color: #000;
            background: #fff;
          }

          h5, h6 {
            color: #000 !important;
            page-break-after: avoid;
          }

          /* Ajustar badges */
          .badge {
            display: inline-block !important;
            border: 1px solid #000;
            color: #000 !important;
            background: #fff !important;
          }

          /* Ajustar tablas */
          .table {
            border: 1px solid #dee2e6;
          }

          .table th,
          .table td {
            border: 1px solid #dee2e6;
            padding: 0.5rem;
          }

          /* Saltos de página */
          .page-break {
            page-break-before: always;
          }

          /* Márgenes de página */
          @page {
            margin: 2cm;
          }

          /* Ocultar sidebar derecho en impresión */
          .col-lg-4 .card {
            display: none;
          }

          /* Mostrar solo contenido principal */
          .col-lg-8 {
            width: 100% !important;
          }
        }

        /* Estilos para vista previa de impresión */
        @media screen {
          .printable-content {
            position: relative;
          }
        }
      `}</style>
    </>
  );
}

export default OrderDetail;
